import moment from 'moment';
import { indexOf } from 'lodash';
import {
  LOGIN, LOGIN_USERS, LOGIN_ERRORS,
  PRETENT_ORGANIZATION, ORGANIZATION_MANAGERS,
  POSTMARK_FETCH_SIGNATURE,
  FETCH_USER_INFO,
  SHOW_COLLECTOR_LANGUAGE_MAPPING_MODAL,
  FETCH_COLLECTOR_LANGUAGE_MAPPINGS,
  UPDATE_EMAIL_ALERT_SUBSCRIPTION,
} from './AuthActions';
import { UPDATE_MY_ORGANIZATION_SYNC_TYPE } from '../App/AppActions';
import { UPDATE_ORGANIZATION_USER, SYNC_ORGANIZATION, REQUEST_SYNC_ORGANIZATION, END_REQUEST_SYNC_ORGANIZATION } from '../Setup/SetupActions';
import { UPDATE_ORGANIZATION } from '../Admin/AdminActions';

// Initial State
const initialState = {
  loginUser: {},
  managers: {
    items: [],
    meta: {},
  },
  loginErrors: {},
  allUsers: [],
  postmarkSignature: null,
  collectorLanguages: {
    mappings: [],
    showModal: false,
    errors: null,
  },
};


const AuthReducer = (state = initialState, action) => {
  switch (action.type) {
    case LOGIN:
      return { ...state, loginUser: action.user || {}, loginErrors: {} };
    case LOGIN_ERRORS:
      return { ...state, loginErrors: action.errors };
    case LOGIN_USERS:
      return { ...state, allUsers: action.users };
    case UPDATE_MY_ORGANIZATION_SYNC_TYPE: {
      const loginUser = {
        ...state.loginUser,
        organization: { ...state.organization, ...action.organization },
      };
      return { ...state, loginUser };
    }
    case ORGANIZATION_MANAGERS: {
      return { ...state, managers: action.managers };
    }
    case UPDATE_ORGANIZATION_USER: {
      if (action.user._id === state.loginUser._id) {
        return { ...state, loginUser: { ...state.loginUser, ...action.user } };
      }
      return state;
    }
    case UPDATE_ORGANIZATION:
    case SYNC_ORGANIZATION: {
      let loginUser = { ...state.loginUser };
      if (loginUser.organization__id === action.organization._id) {
        loginUser = { ...loginUser, organization: { ...action.organization } };
      }
      if (loginUser.pretent_organization_id === action.organization._id) {
        loginUser = { ...loginUser, pretent_organization: { ...action.organization } };
      }
      return { ...state, loginUser };
    }
    case REQUEST_SYNC_ORGANIZATION: {
      const { id } = action;
      const { loginUser } = state;
      let pretentOrg = loginUser.pretent_organization;
      let rootOrg = loginUser.organization;
      if (pretentOrg && (pretentOrg.organization_id === id || pretentOrg._id === id)) {
        pretentOrg = { ...pretentOrg, syncing: true };
      }
      if (rootOrg && (rootOrg.organization_id === id || rootOrg._id === id)) {
        rootOrg = { ...rootOrg, syncing: true };
      }
      const newLoginUser = { ...loginUser, pretent_organization: pretentOrg, organization: rootOrg };
      return { ...state, loginUser: newLoginUser };
    }
    case END_REQUEST_SYNC_ORGANIZATION: {
      const { id } = action;
      const { loginUser } = state;
      let pretentOrg = loginUser.pretent_organization;
      let rootOrg = loginUser.organization;
      if (pretentOrg && (pretentOrg.organization_id === id || pretentOrg._id === id)) {
        pretentOrg = { ...pretentOrg, syncing: false };
      }
      if (rootOrg && (rootOrg.organization_id === id || rootOrg._id === id)) {
        rootOrg = { ...rootOrg, syncing: false };
      }
      const newLoginUser = { ...loginUser, pretent_organization: pretentOrg, organization: rootOrg };
      return { ...state, loginUser: newLoginUser };
    }
    case PRETENT_ORGANIZATION: {
      const loginUser = { ...state.loginUser, pretent_organization: action.organization };
      loginUser.pretent_organization_id = action.organization ? action.organization._id : null;
      return { ...state, loginUser };
    }
    case POSTMARK_FETCH_SIGNATURE: {
      const { signature } = action;
      return { ...state, postmarkSignature: signature };
    }
    case SHOW_COLLECTOR_LANGUAGE_MAPPING_MODAL: {
      const { showModal } = action;
      return {
        ...state,
        collectorLanguages: {
          ...state.collectorLanguages,
          showModal,
        },
      };
    }
    case FETCH_COLLECTOR_LANGUAGE_MAPPINGS: {
      const { mappings, errors } = action;
      return {
        ...state,
        collectorLanguages: {
          ...state.collectorLanguages,
          mappings,
          errors,
        },
      };
    }
    case FETCH_USER_INFO: {
      const { user } = action;
      return {
        ...state,
        loginUser: user,
      };
    }
    case UPDATE_EMAIL_ALERT_SUBSCRIPTION: {
      const loginUser = { ...state.loginUser };
      const { user } = action;
      return {
        ...state,
        loginUser: {
          ...loginUser,
          email_alert_unsubscribe: user.email_alert_unsubscribe,
        }
      };
    }
    default:
      return state;
  }
};

/* Selectors */

// Get login user
export const getUser = state => state.auth.loginUser;
export const getLoginErrors = state => state.auth.loginErrors;
export const getAllUsers = state => state.auth.allUsers;
export const getManagers = state => state.auth.managers;
export const getIsSuperAdmin = state => state.auth.loginUser.role === 'superadmin';
export const getIsAdmin = state => state.auth.loginUser.role === 'admin';
export const getIsPIUser = state => state.auth.loginUser.role === 'pi';
export const getIsOrganizationAdmin = state => state.auth.loginUser.organization_role === 'admin';
export const getIsOrganizationManager = state => state.auth.loginUser.organization_role === 'manager';

export const getIsUsingOrganizationSwitch = (state) => {
  const pretentOrg = state.auth.loginUser.pretent_organization || {};
  const org = state.auth.loginUser.organization || {};
  const diff = pretentOrg._id && pretentOrg._id !== org._id;
  const isAdmin = getIsAdmin(state) || getIsSuperAdmin(state);
  const isPI = getIsPIUser(state);
  if (diff) {
    return isAdmin || (isPI && indexOf(state.auth.loginUser.organization__ids, pretentOrg._id) >= 0);
  }
  return false;
};

export const getUserCurrentOrganization = (state) => {
  if (state.auth.loginUser) {
    return state.auth.loginUser.pretent_organization || state.auth.loginUser.organization;
  }
  return null;
};

export const getCanSetupOrganization = (state) => {
  const isSuperAdmin = getIsSuperAdmin(state);
  const isAdmin = getIsAdmin(state);
  const isUsingOrganizationSwitch = getIsUsingOrganizationSwitch(state);
  const isOrganizationAdmin = getIsOrganizationAdmin(state);
  return isSuperAdmin || isAdmin || isOrganizationAdmin || isUsingOrganizationSwitch;
};

export const getCanAccessToSalesforce = (state) => {
  const user = getUser(state);
  const userCurrentOrganization = getUserCurrentOrganization(state);
  return (
    user.organization.sync_type === 'detailed' &&
    (user.organization.custom_domain || (user.organization.type === 'force' && user.organization.instance_name)));
  /*
  return (user.organization_id === userCurrentOrganization.organization_id &&
    !user.is_none_standard_user &&
    user.organization.sync_type === 'detailed' &&
    (user.organization.custom_domain || (user.organization.type === 'force' && user.organization.instance_name)));
  */
};

export const getSalesforceBaseUrl = (/* state */) => {
  return null; // do not use this function
  /*
  const canLink = getCanAccessToSalesforce(state);
  if (!canLink) {
    return null;
  }
  const user = getUser(state);
  const { organization } = user;
  if (organization.custom_domain) {
    return `${organization.custom_domain}/`;
  } else if (organization.instance_name) {
    return `https://${user.organization.instance_name}.salesforce.com/`;
  }
  return null;
  */
};

export const getCRMBaseUrl = (state) => {
  const user = getUser(state);
  const { organization } = user;
  
  // huspot crmBaseUrl is always same
  if (organization.type === 'hubspot') {
    return 'https://app.hubspot.com/';
  }

  const canLink = getCanAccessToSalesforce(state);
  if (!canLink) {
    return null;
  }

  if (organization.custom_domain) {
    return `${organization.custom_domain}`;
  } 
  if (organization.type === 'force' && organization.instance_name) {
    return `https://${user.organization.instance_name}.salesforce.com/`;
  }
  return null;
};

export const getOrganizationLastSyncDate = (state) => {
  const org = getUserCurrentOrganization(state);
  if (org && org.last_synced_date) {
    return moment(org.last_synced_date);
  }
  return null;
};
export const getPostmarkSignature = state => state.auth.postmarkSignature;

export const getCollectorLanguageMappings = state => (state.auth.collectorLanguages ? state.auth.collectorLanguages.mappings : []);
export const getCollectorLanguageShowModal = state => (state.auth.collectorLanguages ? state.auth.collectorLanguages.showModal : false);


// Export Reducer
export default AuthReducer;
